/* global React, Icon, KPI, SearchField, DataList, Pagination, fmt, t */
const { useEffect: ishUseEffect, useState: ishUseState } = React;

function ImportedSalesHistory({ year }) {
  const [query, setQuery] = ishUseState('');
  const [page, setPage] = ishUseState(1);
  const [pageSize, setPageSize] = ishUseState(10);
  const [expanded, setExpanded] = ishUseState(null);
  const [result, setResult] = ishUseState({ rows: [], total: 0, pages: 1, summary: null });
  const [loading, setLoading] = ishUseState(true);
  const [error, setError] = ishUseState('');

  ishUseEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await window.PlummeBackend?.getAccountingImportSalesHistory?.({ year, query, page, pageSize });
        if (active) setResult(payload || { rows: [], total: 0, pages: 1, summary: null });
      } catch (requestError) {
        if (active) setError(requestError?.message || t('inv.importHistory.error'));
      } finally {
        if (active) setLoading(false);
      }
    }, query ? 250 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [year, query, page, pageSize]);

  ishUseEffect(() => { setPage(1); setExpanded(null); }, [year, query, pageSize]);

  if (loading && !result.rows.length) return <div className="col center" style={{ padding: '34px 20px' }}><p className="muted">{t('inv.importHistory.loading')}</p></div>;
  if (error) return <div className="bg-note warn"><Icon name="alertTriangle" size={18} /><span>{error}</span></div>;

  const summary = result.summary;
  return <div className="col gap-16 mt-20">
    <div className="bg-note info"><Icon name="archive" size={18} /><span>{t('inv.importHistory.readOnly')}</span></div>
    <div className="grid-4">
      <KPI tint="plum" icon="fileText" label={t('inv.importHistory.documents')} value={summary?.documentCount || 0} sub={t('inv.importHistory.year', { year })} />
      <KPI tint="green" icon="trendUp" label={t('inv.importHistory.netSales')} value={fmt(summary?.revenue || 0)} sub={t('inv.kpi.sub.exVat')} />
      <KPI tint="white" icon="users" label={t('inv.importHistory.customers')} value={summary?.customerCount || 0} sub={t('inv.kpi.sub.uniqueCustomers')} />
      <KPI tint="peach" icon="files" label={t('inv.importHistory.creditNotes')} value={summary?.creditNoteCount || 0} sub={t('inv.importHistory.netIncluded')} />
    </div>
    <div className="fak-list-tools"><SearchField value={query} onChange={setQuery} placeholder={t('inv.importHistory.search')} /></div>
    {!result.total ? <div className="col center" style={{ padding: '30px 20px', textAlign: 'center' }}><h3 className="t-h2">{t('inv.importHistory.empty')}</h3><p className="muted">{t('inv.importHistory.emptySub')}</p></div> : <React.Fragment>
      <DataList tableClass="fak-tbl" rows={result.rows || []} paginate={false} rowKey={(row) => row.id}
        onRowClick={(row) => setExpanded(expanded === row.id ? null : row.id)} expandedKey={expanded}
        renderExpanded={(row) => <div className="col gap-8" style={{ padding: 16 }}>
          <b>{t('inv.importHistory.detailTitle')}</b>
          <span data-help={t('helpTopics.dueDate.body')} data-help-title={t('helpTopics.dueDate.title')}>{t('inv.importHistory.dueDate')}: {row.dueDate || '—'}</span>
          <span>{t('inv.importHistory.paidDate')}: {row.paidDate || '—'}</span>
          <span data-help={t('helpTopics.openItem.body')} data-help-title={t('helpTopics.openItem.title')}>{t('inv.importHistory.remaining')}: {fmt(row.remainingAmount || 0)}</span>
          {row.lines?.length ? <div><b>{t('inv.importHistory.lines')}</b>{row.lines.map((line, index) => <div key={index}>{line.description} · {fmt(line.netAmount || 0)}</div>)}</div> : <span className="muted">{t('inv.importHistory.noLines')}</span>}
        </div>}
        columns={[
          { header: t('inv.col.invoice'), render: (row) => <div className="row gap-12"><span className="icon-chip fak-ico"><Icon name={row.documentKind === 'creditNote' ? 'files' : 'fileText'} size={18} /></span><div><div className="t-h3" style={{ fontSize: 14.5 }}>{row.invoiceNumber}</div><div className="t-sm muted">{row.customerName}</div></div></div> },
          { header: t('inv.col.date'), render: (row) => row.date },
          { header: t('inv.col.net'), headerRight: true, cellClass: 'num', render: (row) => fmt(row.netAmount) },
          { header: t('inv.col.amount'), headerRight: true, cellClass: 'num', render: (row) => fmt(row.grossAmount) },
          { header: t('inv.col.status'), render: (row) => <span className="badge neutral">{row.status === 'paid' ? t('inv.status.paid') : t('inv.status.unpaid')}</span> },
        ]} />
      <Pagination page={result.page || page} pages={result.pages || 1} total={result.total} from={(result.page - 1) * pageSize + 1} to={Math.min(result.page * pageSize, result.total)} onPage={setPage} pageSize={pageSize} onPageSize={setPageSize} pageSizes={[10, 50, 100]} />
    </React.Fragment>}
  </div>;
}

Object.assign(window, { ImportedSalesHistory });
